'use client'
import { Skeleton } from '@morpho-org/ui-v6/server'

interface HeroMarketLoadingProps {
  loanSymbol?: string
  /** `null` for an idle market, which has no collateral side. */
  collateralSymbol?: string | null
}

/**
 * Keep in step with `HeroMarketSection.client.tsx`. Known symbols render invisibly inside
 * their blocks so the title wraps like the loaded hero.
 */
const HeroMarketLoading = ({ loanSymbol, collateralSymbol }: HeroMarketLoadingProps) => {
  return (
    <div
      data-testid="market-hero"
      className="flex w-full flex-col items-start gap-[48px] max-sm:gap-[60px]"
    >
      <div className="gap-sm flex flex-col">
        <div data-testid="market-hero-title" className="gap-xs flex flex-wrap items-center">
          {/* The title pairs each symbol with its own 60px avatar (38px on phones),
              separated by the decorative rule. */}
          <div className="gap-sm max-sm:gap-xs flex flex-wrap items-center">
            <HeroTitlePart symbol={loanSymbol} withTrailingRule={collateralSymbol !== null} />
            {collateralSymbol !== null && <HeroTitlePart symbol={collateralSymbol} />}
          </div>
          <div className="gap-2xs flex flex-wrap items-center">
            <Skeleton className="h-[18px] w-[37px] rounded-[20px]" />
          </div>
        </div>

        {/* Market ID `ClipboardButton` and chain `Button`, both 32px tall. The ID width is fixed;
            the chain width is measured on "Ethereum". */}
        <div className="gap-sm flex flex-wrap items-center">
          <Skeleton className="h-lg w-[137px] rounded-[20px]" />
          <Skeleton className="h-lg w-[107px] rounded-[20px]" />
        </div>
      </div>
      <div className="gap-xl flex w-full flex-col">
        <div className="gap-lg flex w-full flex-wrap justify-start max-sm:flex-col">
          <HeroDataCard withSubValue />
          <HeroDataCard withSubValue />
          <HeroDataCard />
          <HeroDataCard />
        </div>

        {/* `MarketTrustedBy` for a market trusted by one curator, the case on most visited market
            pages: a 38px curator logo with the stack's 10px trailing space, then one line of
            "Trusted by" copy. Markets with more curators or no curators render a different row
            height. */}
        <div className="gap-sm flex h-[38px] items-center">
          <Skeleton variant="circle" className="mr-[10px] size-[38px]" />
          <Skeleton className="h-[16px] w-[202px] rounded-[20px]" />
        </div>
      </div>
    </div>
  )
}

interface HeroTitlePartProps {
  symbol?: string
  withTrailingRule?: boolean
}

/** One avatar and symbol of the market title; sized to the `text-title-md` / `text-title-sm` line heights. */
const HeroTitlePart = ({ symbol, withTrailingRule = false }: HeroTitlePartProps) => {
  return (
    <div className="gap-sm max-sm:gap-xs flex items-center">
      <Skeleton variant="circle" className="size-[60px] shrink-0 max-sm:size-[38px]" />
      {symbol ? (
        <div className="relative">
          <span className="text-title-md max-sm:text-title-sm invisible block break-all">
            {symbol}
          </span>
          <Skeleton className="absolute inset-0 rounded-[20px]" />
        </div>
      ) : (
        <Skeleton className="h-(--text-title-md--line-height) w-[140px] rounded-[20px] max-sm:h-(--text-title-sm--line-height) max-sm:w-[110px]" />
      )}
      {withTrailingRule && <Skeleton className="h-lg max-sm:h-md w-0.5 rounded-full" />}
    </div>
  )
}

interface HeroDataCardProps {
  withSubValue?: boolean
}

const HeroDataCard = ({ withSubValue = false }: HeroDataCardProps) => {
  return (
    <div className="gap-sm flex flex-[1px] flex-col">
      <Skeleton className="h-md w-[150px] rounded-[20px]" />
      <div className="gap-xs flex flex-col">
        <Skeleton className="h-(--text-title-sm--line-height) w-[170px] rounded-[20px]" />
        {withSubValue && (
          <Skeleton className="h-(--text-sm--line-height) w-[65px] rounded-[20px]" />
        )}
      </div>
    </div>
  )
}

export default HeroMarketLoading
